import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { HugeiconsIcon } from '@hugeicons/react'
import { Cancel01StrokeRounded } from '@hugeicons-pro/core-stroke-rounded'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { TbxTooltip } from '@/common/components'

/**
 * DocumentsDialog - The documents list for one quote item, in a dialog opened from the item card.
 * The caller passes the item's panel as children. Children mount only while the dialog is open.
 *
 * The X and Escape close it. A backdrop click does not, so a stray click beside the list never
 * loses the user's place.
 *
 * Render it outside the item's AccordionSummary: React events from a portal still bubble through
 * the React tree, and a click inside the dialog would otherwise toggle the item.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - the item's attachments panel
 * @param {string} props.itemName
 * @param {Function} props.onClose
 * @param {boolean} props.open
 * @param {string} props.subtitle - says which item the documents belong to
 */
const DocumentsDialog = ({ children, itemName, onClose, open, subtitle }) => {
    const { t } = useTranslation()
    const titleId = useId()

    return (
        <Dialog
            aria-labelledby={titleId}
            maxWidth="xl"
            open={open}
            fullWidth
            onClose={(_event, reason) => {
                if (reason !== 'backdropClick') onClose()
            }}
        >
            {/* DialogTitle takes the dialog's aria-labelledby as its id by default; give it its own
                so the dialog is named by the item name alone. */}
            <DialogTitle
                component="div"
                id={`${titleId}-header`}
                sx={{ alignItems: 'flex-start', display: 'flex', gap: 2, justifyContent: 'space-between' }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        component="h2"
                        id={titleId}
                        title={itemName}
                        variant="h6"
                        noWrap
                    >
                        {itemName}
                    </Typography>
                    <Typography
                        color="text.secondary"
                        variant="body2"
                    >
                        {subtitle}
                    </Typography>
                </Box>
                <TbxTooltip title={t('Close')}>
                    <IconButton
                        aria-label={t('Close')}
                        onClick={onClose}
                    >
                        <HugeiconsIcon
                            icon={Cancel01StrokeRounded}
                            size={20}
                        />
                    </IconButton>
                </TbxTooltip>
            </DialogTitle>
            <DialogContent sx={{ pb: 3, px: 1.5 }}>{children}</DialogContent>
        </Dialog>
    )
}

DocumentsDialog.propTypes = {
    children: PropTypes.node.isRequired,
    open: PropTypes.bool.isRequired,
    subtitle: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    itemName: PropTypes.string,
}

export default DocumentsDialog
